import { useEffect, useState } from 'react';
import { getVideoServers, getServerRoute, createFolder, deleteFolder } from '../../../api';
import useRouteStore from '../../../hooks/useRouteStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer } from '@fortawesome/free-solid-svg-icons';
import { faFolder } from '@fortawesome/free-solid-svg-icons';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faVideo } from '@fortawesome/free-solid-svg-icons';
import Video from './Video/Video';
import Uploader from './Uploader';
import { faFileArrowUp } from '@fortawesome/free-solid-svg-icons';
import SearchBar from '../../../common/SearchBar/SearchBar';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { swalConfig, toastConfig } from '../../../config';
import { faFolderPlus } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';

const Videos = () => {
  const mySwal = withReactContent(Swal);

  const setRoute = useRouteStore((state) => state.setRoute);
  const [folders, setFolders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [servers, setServers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedServer, setSelectedServer] = useState(0);
  const [serverRoute, setServerRoute] = useState([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [videoClicked, setVideoClicked] = useState(null);
  const [lockDeleteFolder, setLockDeleteFolder] = useState(false);

  const regex = new RegExp(search.toLowerCase(), 'i');

  useEffect(() => {
    setRoute([
      { name: 'Inicio', url: '/administracion' },
      { name: 'Videos', url: '/administracion/videos' },
    ]);

    getVideoServers().then((res) => {
      setServers(res.data);
      setLoading(false);
      setRouteLoading(false);
    });
  }, []);

  useEffect(() => {
    if (servers.length) {
      setRouteLoading(true);
      getServerRoute({ path: `/${serverRoute.join('/')}`, serverId: servers[selectedServer]._id }).then((res) => {
        setSearch('');
        setVideoClicked(null);
        setFolders(res.data.folders);
        setVideos(res.data.files);
        setRouteLoading(false);
      });
    }
  }, [selectedServer, serverRoute, servers]);

  const returnToPreviousFolder = () => {
    if (serverRoute.length) {
      const newRoute = [...serverRoute];
      newRoute.pop();
      setServerRoute(newRoute);
    }
  };

  const handleServerSelection = (index) => {
    // Reset serverRoute
    setServerRoute([]);
    // Set selected server
    setSelectedServer(index);
    setSearch('');
  };

  const removeVideo = (name) => {
    setVideos(videos.filter((video) => video.name !== name));
    setVideoClicked(null);
  };

  const refreshRoute = (path) => {
    if (path !== serverRoute.join('/')) return;
    setRouteLoading(true);
    setSearch('');
    getServerRoute({ path: `/${serverRoute.join('/')}`, serverId: servers[selectedServer]._id }).then((res) => {
      setFolders(res.data.folders);
      setVideos(res.data.files);
      setRouteLoading(false);
    });
  };

  const handleDeleteFolder = async () => {
    try {
      if (routeLoading) return;
      const result = await mySwal.fire({
        ...swalConfig.delete,
        text: `Estas borrando la carpeta "/${serverRoute.join('/')}"`,
        allowOutsideClick: () => !Swal.isLoading(),
      });

      if (result.isConfirmed) {
        // mySwal.showLoading();
        await deleteFolder({ serverId: servers[selectedServer]._id, folderpath: serverRoute.join('/') });
        serverRoute.pop();
        setServerRoute([...serverRoute]);
        toast.success('Carpeta borrada', toastConfig);
      }
    } catch (err) {
      console.log(err);
      toast.error('Error al borrar carpeta', toastConfig);
    }
  };

  const handleCreateFolder = async () => {
    try {
      if (routeLoading) return;
      const result = await mySwal.fire({ ...swalConfig.input, text: 'Crear carpeta', allowOutsideClick: () => !Swal.isLoading() });

      if (result.isConfirmed) {
        // mySwal.showLoading();
        const { data } = await createFolder({
          folderPath: "/" + serverRoute.join('/'),
          serverId: servers[selectedServer]._id,
          folder: result.value,
        });
        toast.success('Carpeta creada', toastConfig);
        setServerRoute([...serverRoute, data.folder]);
      }
    } catch (err) {
      console.log(err);
      toast.error('Error al crear la carpeta', toastConfig);
    }
  };

  return (
    <div className='font-montserrat w-full max-h-full min-h-[20rem] relative'>
      {!loading ? (
        <>
          <h1 className='font-semibold text-xl mb-2'>Videos</h1>
          <div className='flex gap-3 mb-3'>
            {servers.map((server, index) => {
              const usedPercentage = 100 - Math.round((server.usage.available / server.usage.total) * 100);
              const availableGigs = Math.round(server.usage.available / 1000000000);
              const usageColor = usedPercentage > 80 ? 'bg-red-600' : 'bg-green-600';
              const extraClasses = selectedServer === index ? 'ring-[1px]' : 'hover:ring-[2px] ring-red-1';
              return (
                <button
                  onClick={() => handleServerSelection(index)}
                  key={index}
                  className={'grid grid-cols-5 p-3 w-48 rounded-lg bg-off-white text-red-1 transition-all ' + extraClasses}
                >
                  <div className='flex items-center gap-3 col-span-5'>
                    <FontAwesomeIcon icon={faServer} />
                    <h1 className='text-lg font-semibold'>{server.name}</h1>
                  </div>
                  <div className='flex flex-col justify-start col-span-5'>
                    <span className='w-fit'>{server?.usage?.available ? `Disponible: ${availableGigs}Gb` : 'Uso no disponible'}</span>
                    {server?.usage?.available ? (
                      <div className='h-2 bg-white rounded-xl overflow-hidden mt-1'>
                        <div className={'h-full ' + usageColor} style={{ width: `${usedPercentage}%` }}></div>
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
          <div className='flex gap-2 w-full'>
            <div className='flex items-center w-full rounded-lg p-1 px-2 bg-red-2 bg-opacity-5 text-red-1 font-semibold '>
              <span>/{serverRoute.join('/')}</span>
            </div>
            <div className='flex gap-1'>
              <button
                onClick={returnToPreviousFolder}
                className='bg-red-1 text-white h-full w-8 text-center p-2 hover:text-red-1 hover:bg-white rounded-lg transition-all'
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <button
                onClick={handleCreateFolder}
                className='bg-red-1 text-white h-full w-8 text-center p-2 hover:text-red-1 hover:bg-white rounded-lg transition-all'
              >
                <FontAwesomeIcon icon={faFolderPlus} />
              </button>
            </div>
          </div>
          <Uploader
            limit={1}
            serverId={servers[selectedServer]._id}
            currentPath={serverRoute}
            refresh={refreshRoute}
            setLockDeleteFolder={setLockDeleteFolder}
            directoryFiles={videos}
            directoryFolders={folders}
          />
          <div className='w-full py-2 '>
            <div className='flex gap-3 w-full'>
              <input
                type='text'
                placeholder='Buscar...'
                onChange={(e) => setSearch(e.target.value)}
                value={search}
                className='transition-colors border-2 w-full rounded h-10 leading-10 focus:border-red-2 focus:outline-none pl-3'
              />
            </div>
          </div>
          <div style={{ display: !routeLoading ? 'flex' : 'none' }} className='flex flex-col mt-2 gap-1 max-w-full'>
            {folders.map((folder, index) => {
              if (!regex.test(folder.name.toLowerCase())) return null;
              return (
                <button
                  key={index}
                  onClick={() => setServerRoute([...serverRoute, folder.name])}
                  className='flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100 hover:border-red-2 hover:shadow-sm transition-all text-sm font-medium text-gray-700 hover:text-red-1'
                >
                  <FontAwesomeIcon icon={faFolder} className='text-amber-400 text-base flex-shrink-0' />
                  <span className='truncate'>{folder.name}</span>
                </button>
              );
            })}
            {videos.map((video, index) => {
              if (!regex.test(video.name.toLowerCase())) return null;
              return videoClicked === index ? (
                <Video
                  key={index}
                  data={video}
                  removeVideo={removeVideo}
                  currentPath={serverRoute}
                  serverId={servers[selectedServer]._id}
                  refresh={refreshRoute}
                />
              ) : (
                <button
                  key={index}
                  onClick={() => setVideoClicked(index)}
                  className='flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100 hover:border-red-2 hover:shadow-sm transition-all text-sm font-medium text-gray-700 hover:text-red-1'
                >
                  <FontAwesomeIcon icon={faVideo} className='text-red-1 text-base flex-shrink-0' />
                  <span className='truncate'>{video.name}</span>
                </button>
              );
            })}
            {videos.length === 0 && folders.length === 0 && !lockDeleteFolder ? (
              <button
                onClick={handleDeleteFolder}
                className='flex items-center gap-3 px-3 py-2 rounded-lg border border-dashed border-red-200 text-red-1 hover:bg-red-1 hover:text-white hover:border-red-1 transition-all text-sm font-medium'
              >
                <FontAwesomeIcon icon={faTrashCan} className='flex-shrink-0' />
                <span>Borrar esta carpeta</span>
              </button>
            ) : null}
          </div>
          <div style={{ display: routeLoading ? 'flex' : 'none' }}>
            <span className='loader2'></span>
          </div>
        </>
      ) : (
        <div className='h-14 mt-20'>
          <span className='loader2'></span>
        </div>
      )}
    </div>
  );
};

export default Videos;
